import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { RootState, AppDispatch } from '../store';
import {
  setProfileVisibility,
  setProfileStyle,
  setProfileColor,
  setGradientLength,
  GradientLength,
  ProfileStyle,
  setShowGradientEffect,
  setChartHeight,
} from '../store/profileSlice';
import { Radio, RadioGroup, Field, Input, Label, Switch } from '@headlessui/react';
import clsx from 'clsx';
import { FaChartArea, FaChartLine } from 'react-icons/fa'; // Example icons
import { HiShoppingCart } from 'react-icons/hi'; // Import HiShoppingCart icon
import { debounce } from 'lodash';
import { useAddToCart } from '../utils/useAddToCart';

interface ProfileProps {
  editorPreviewRef: React.RefObject<any>;
}

const Profile: React.FC<ProfileProps> = ({ editorPreviewRef }) => {
  const { t } = useTranslation(); // Initialize useTranslation
  const dispatch: AppDispatch = useDispatch();
  const { isVisible, style, color, gradientLength, showGradientEffect, chartHeight } = useSelector(
    (state: RootState) => state.profile
  );

  // --- Local state for debounced color input ---
  const [localColor, setLocalColor] = useState(color);
  const [localChartHeight, setLocalChartHeight] = useState(chartHeight);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced actions
  const debouncedSetProfileColor = useCallback(
    debounce((color: string) => dispatch(setProfileColor(color)), 200),
    [dispatch]
  );
  const debouncedSetChartHeight = useCallback(
    debounce((height: number) => dispatch(setChartHeight(height)), 200),
    [dispatch]
  );

  // Update local state when Redux state changes
  useEffect(() => {
    setLocalColor(color);
  }, [color]);

  useEffect(() => {
    setLocalChartHeight(chartHeight);
  }, [chartHeight]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handlers that update local state and call debounced/direct actions
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalColor(e.target.value);
    debouncedSetProfileColor(e.target.value);
  };

  const handleChartHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setLocalChartHeight(value);
    debouncedSetChartHeight(value);
  };

  const handleToggleVisibility = (checked: boolean) => {
    dispatch(setProfileVisibility(checked));
  };

  const handleStyleChange = (style: 'area' | 'line') => {
    dispatch(setProfileStyle(style));
  };
  
  const handleGradientLengthChange = (length: GradientLength) => {
    dispatch(setGradientLength(length));
  };

  const handleToggleGradientEffect = (checked: boolean) => {
    dispatch(setShowGradientEffect(checked));
  };

  const gradientOptions: GradientLength[] = ['short', 'medium', 'long'];

  const { handleAddToCart, isAddingToCart, isGeneratingPreview, uploadProgress, cartOverlay } = useAddToCart(editorPreviewRef);

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-white break-words whitespace-normal">{t('pageTitles.profile')}</h1>
        <p className="text-sm text-gray-400 mt-1 break-words whitespace-normal">
          {t('profilePage.subtitle')}
        </p>
      </div>

      {/* Visibility Toggle */}
      <Field className="flex items-center justify-between">
        <Label className="text-sm font-medium text-white">{t('profilePage.showElevation')}</Label>
        <Switch
          checked={isVisible}
          onChange={handleToggleVisibility}
          className={clsx(
            'group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900',
            isVisible ? 'bg-blue-600' : 'bg-gray-500'
          )}
        >
          <span
            aria-hidden="true"
            className={clsx(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              isVisible ? 'translate-x-5' : 'translate-x-0'
            )}
          />
        </Switch>
      </Field>

      {isVisible && (
        <>
          {/* Style (Area/Line) */}
          <Field>
            <Label className="text-sm font-medium text-white">{t('profilePage.style')}</Label>
            <RadioGroup value={style} onChange={handleStyleChange} className="mt-1">
              <div className="grid grid-cols-2 gap-2">
                <Radio
                  value="area"
                  className={({ checked }) =>
                    clsx(
                      'cursor-pointer rounded-lg p-3 text-center text-sm font-medium bg-white/5 text-white flex items-center justify-center gap-2',
                      checked && 'ring-2 ring-blue-500 bg-white/10',
                      'focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white'
                    )
                  }
                >
                  <span className="flex items-center gap-2">
                    <FaChartArea className="w-5 h-5" />
                    {t('profilePage.area')}
                  </span>
                </Radio>
                <Radio
                  value="line"
                  className={({ checked }) =>
                    clsx(
                      'cursor-pointer rounded-lg p-3 text-center text-sm font-medium bg-white/5 text-white flex items-center justify-center gap-2',
                      checked && 'ring-2 ring-blue-500 bg-white/10',
                      'focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white'
                    )
                  }
                >
                  <span className="flex items-center gap-2">
                    <FaChartLine className="w-5 h-5" />
                    {t('profilePage.line')}
                  </span>
                </Radio>
              </div>
            </RadioGroup>
          </Field>

          {/* Show Gradient Effect Toggle (only for area style) */}
          {style === 'area' && (
            <Field className="flex items-center justify-between">
              <Label className="text-sm font-medium text-white">{t('profilePage.showGradientEffect', 'Effet dégradé')}</Label>
              <Switch
                checked={showGradientEffect}
                onChange={handleToggleGradientEffect}
                className={clsx(
                  'group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900',
                  showGradientEffect ? 'bg-blue-600' : 'bg-gray-500'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    showGradientEffect ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </Switch>
            </Field>
          )}

          {/* Gradient Length (only for area style) */}
          {style === 'area' && (
            <Field>
              <Label className="text-sm font-medium text-white">{t('profilePage.gradientLength')}</Label>
              <RadioGroup value={gradientLength} onChange={handleGradientLengthChange} className="mt-1">
                <div className="grid grid-cols-3 gap-2">
                  {gradientOptions.map((option) => (
                    <Radio
                      key={option}
                      value={option}
                      className={({ checked }) =>
                        clsx(
                          'cursor-pointer rounded-lg p-3 text-center text-sm font-medium bg-white/5 text-white',
                          checked && 'ring-2 ring-blue-500 bg-white/10',
                          'focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white'
                        )
                      }
                    >
                      {t(`profilePage.${option}`)}
                    </Radio>
                  ))}
                </div>
              </RadioGroup>
            </Field>
          )}

          {/* Chart Height Slider */}
          <Field>
            <Label className="text-sm font-medium text-white flex justify-between items-center">
              <span>{t('profilePage.chartHeight')}</span>
              <span className="text-xs text-gray-300">{localChartHeight} px</span>
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min="30"
                max="200"
                value={localChartHeight}
                onChange={handleChartHeightChange}
                className="w-full h-2 bg-blue-500 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <Input
                type="number"
                min="30"
                max="200"
                value={localChartHeight}
                onChange={handleChartHeightChange}
                className="w-16 rounded-md border-none bg-white/10 text-sm px-2 py-1 text-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </Field>

          {/* Color Picker */}
          <Field>
            <Label className="text-sm font-medium text-white">{t('tracePage.color')}</Label>
            <div className="mt-1 flex items-center">
              <input
                type="color"
                value={localColor}
                onChange={handleColorChange}
                className="w-8 h-8 rounded-md border-gray-600 cursor-pointer focus:ring-blue-500 focus:border-blue-500 bg-transparent mr-2"
              />
              <Input
                type="text"
                value={localColor}
                onChange={handleColorChange}
                className="block w-full rounded-md bg-white/5 py-1.5 px-3 text-sm/6 text-white focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
              />
            </div>
          </Field>

          {cartOverlay}
          {isVisible && (
            <>
              {/* ...existing code... */}
              <button
                onClick={handleAddToCart}
                disabled={isAddingToCart}
                className="w-full mt-6 flex justify-center items-center space-x-2 text-white py-2 rounded-sm text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                type="button"
                style={{ backgroundColor: isAddingToCart ? '#FC4C02' : '#FC4C02', opacity: isAddingToCart ? 0.5 : 1 }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
              >
                <HiShoppingCart className="w-4 h-4" />
                <span>{t('common.addToCart', 'Ajouter au panier')}</span>
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Profile;